/**
 * PostHog error-tracking denylist (HAL-234).
 *
 * A pure, dependency-free predicate that decides whether a PostHog `$exception`
 * event is known third-party / browser-extension / vendor-script noise that
 * should be dropped before it ever reaches PostHog.
 *
 * WHY: PostHog error tracking (project 229038) is ~98% noise we do not author –
 * browser-extension captures, WordPress-plugin load-order errors, and vendor
 * scripts (HubSpot, GTM, reCAPTCHA, Usercentrics). The denylist below removes
 * the highest-volume, unambiguously-not-ours fingerprints at ingest so the
 * team triages a real signal.
 *
 * This module is the SINGLE SOURCE OF TRUTH for the drop rules. It is consumed
 * two ways:
 *   1. `apps/landing` passes `shouldDropExceptionEvent` directly to posthog-js
 *      `before_send` (it has the npm SDK, so a real function works).
 *   2. `packages/generators/before-send-script` emits an inline ES5 mirror of
 *      this logic (embedding the same pattern arrays) for the array.js snippet
 *      injected into `create` (Next.js) and `enhance` (Cloudflare Worker) sites.
 * `before-send-script.spec.ts` runs the emitted JS against the same fixture
 * matrix as this module's spec, so the two implementations cannot drift.
 *
 * SCOPE NOTE: `/wp-content/plugins/…` ChunkLoadErrors are intentionally NOT in
 * the source denylist. Those are asset-cache / script-injection seams Halcy owns
 * and are tracked separately – they must keep ingesting. They are excluded only
 * from the saved "Halcy-owned errors" view (Axis B), not dropped at ingest.
 */

/** PostHog event name for captured exceptions. */
export const EXCEPTION_EVENT_NAME = '$exception';

/**
 * Message/value/type patterns (matched case-insensitively) that identify
 * known-junk exceptions regardless of stack-frame source. These cover the
 * `source: null` captures where frame-path matching cannot help.
 *
 * Each entry is a regular-expression source string. Keep these broad enough to
 * catch id/variant suffixes but specific enough that no genuine app error
 * matches.
 */
export const EXCEPTION_MESSAGE_DENYLIST_PATTERNS: readonly string[] = [
  // MS Editor / Outlook Safe-Links browser extension. Full value looks like
  // "…Object Not Found Matching Id:7, MethodName:update, ParamCount:4" (~183/mo).
  'Object Not Found Matching Id',
  // Benign browser layout notification, both spelling variants (~58/mo).
  'ResizeObserver loop',
  // Non-Error objects thrown by third-party / extension code and captured by
  // posthog as "'CustomEvent' captured as exception with keys: …" etc. Covers
  // CustomEvent / HTMLLinkElement / Event / HTMLScriptElement / XMLHttpRequest
  // (~166/mo). These are never genuine app errors (a real error is an Error).
  'captured as exception with keys',
  // Mobile autofill browser extension (~18/mo). Surfaces as
  // "Can't find variable: _AutofillCallbackHandler".
  '_AutofillCallbackHandler',
];

/**
 * Stack-frame source/filename patterns (matched case-insensitively) that
 * identify vendor scripts whose errors are never ours. Matched against the
 * legacy flat source and each exception's innermost frame `source`, `filename`,
 * `module`, and raw-frame filename.
 *
 * Deliberately excludes `/wp-content/plugins/` – see the SCOPE NOTE above.
 */
export const EXCEPTION_SOURCE_DENYLIST_PATTERNS: readonly string[] = [
  // HubSpot collected-forms tracker: 'Level "ERROR" is not supported' (~62/mo).
  'collectedforms\\.js',
  // Google Tag Manager: "googletag is not defined", "reading 'length'",
  // "require is not defined" (~78/mo). Served from googletagmanager.com/gtm.js.
  'googletagmanager\\.com',
  '/gtm\\.js',
  // Google reCAPTCHA internal errors, e.g. "R9" (~9/mo).
  '/recaptcha/',
  'recaptcha__',
  // Usercentrics consent-management platform module-load failures (~8/mo).
  // Served from app.usercentrics.eu.
  'usercentrics',
  // ct-bot-detector bot-detection script DataCloneError (~12/mo).
  'ct-bot-detector',
];

/** Minimal structural shape of a posthog-js capture result. */
export interface DenylistExceptionEvent {
  event?: string | null;
  properties?: Record<string, unknown> | null;
}

const MESSAGE_REGEXES: readonly RegExp[] = EXCEPTION_MESSAGE_DENYLIST_PATTERNS.map(
  (src) => new RegExp(src, 'i'),
);
const SOURCE_REGEXES: readonly RegExp[] = EXCEPTION_SOURCE_DENYLIST_PATTERNS.map(
  (src) => new RegExp(src, 'i'),
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function pushString(bucket: string[], value: unknown): void {
  if (typeof value === 'string' && value.length > 0) bucket.push(value);
}

function pushStringArray(bucket: string[], value: unknown): void {
  if (Array.isArray(value)) {
    for (const item of value) pushString(bucket, item);
  }
}

/**
 * Collect every message-like string from an exception event: the plural
 * `$exception_values`/`$exception_types`, the legacy flat `$exception_message`,
 * and each `$exception_list[].type`/`.value`.
 *
 * @param properties - The `event.properties` bag from a posthog-js capture result.
 * @returns Every message-like string found, in no particular order.
 */
export function collectExceptionMessages(
  properties: Record<string, unknown>,
): string[] {
  const messages: string[] = [];
  pushStringArray(messages, properties['$exception_values']);
  pushStringArray(messages, properties['$exception_types']);
  pushString(messages, properties['$exception_message']);

  const list = properties['$exception_list'];
  if (Array.isArray(list)) {
    for (const entry of list) {
      if (!isRecord(entry)) continue;
      pushString(messages, entry['type']);
      pushString(messages, entry['value']);
    }
  }
  return messages;
}

/**
 * Collect source paths from an exception event: the legacy flat
 * `$exception_source` plus each exception entry's innermost frame `source`,
 * `filename`, `module`, and the raw-frame filename PostHog nests under
 * `junk_drawer.raw_frame`.
 *
 * @param properties - The `event.properties` bag from a posthog-js capture result.
 * @returns Source/filename/module strings from the flat source and innermost
 *   frame of every `$exception_list` entry.
 */
export function collectExceptionSources(
  properties: Record<string, unknown>,
): string[] {
  const sources: string[] = [];
  pushString(sources, properties['$exception_source']);

  const list = properties['$exception_list'];
  if (!Array.isArray(list)) return sources;

  for (const entry of list) {
    if (!isRecord(entry)) continue;
    const stacktrace = entry['stacktrace'];
    if (!isRecord(stacktrace)) continue;
    const frames = stacktrace['frames'];
    if (!Array.isArray(frames)) continue;

    if (frames.length === 0) continue;

    // Browser-emitted PostHog frames are outermost-first, so the final frame is
    // the throw origin. Do not scan backward: a vendor wrapper must not suppress
    // a genuine first-party throw, even when the final frame is malformed.
    const frame = frames[frames.length - 1];
    if (!isRecord(frame)) continue;
    pushString(sources, frame['source']);
    pushString(sources, frame['filename']);
    pushString(sources, frame['module']);
    const junkDrawer = frame['junk_drawer'];
    if (isRecord(junkDrawer)) {
      const rawFrame = junkDrawer['raw_frame'];
      if (isRecord(rawFrame)) pushString(sources, rawFrame['filename']);
    }
  }
  return sources;
}

/**
 * Decide whether a PostHog capture result is known third-party noise that
 * should be dropped. Only `$exception` events are ever eligible; everything
 * else (pageviews, custom events, `null`) passes through untouched.
 *
 * Fail-open by design: any malformed input returns `false` (keep the event) so
 * a filter bug can never silently swallow a genuine error.
 *
 * @param event - A posthog-js capture result (or `null`/`undefined`).
 * @returns `true` if the event should be dropped before it reaches PostHog,
 *   `false` if it should pass through.
 */
export function shouldDropExceptionEvent(
  event: DenylistExceptionEvent | null | undefined,
): boolean {
  if (!event || event.event !== EXCEPTION_EVENT_NAME) return false;
  const { properties } = event;
  if (!isRecord(properties)) return false;

  const messages = collectExceptionMessages(properties);
  if (messages.some((message) => MESSAGE_REGEXES.some((re) => re.test(message)))) {
    return true;
  }

  const sources = collectExceptionSources(properties);
  if (sources.some((source) => SOURCE_REGEXES.some((re) => re.test(source)))) {
    return true;
  }

  return false;
}
